import { useCallback } from 'react'
import useTrackEvent from './useTrackEvent'
import mParticleSdk, { EventType, SDKEventCustomFlags, SDKEventOptions } from '@mparticle/web-sdk'
import { useInternalAnalytics } from '../../../utils/internalAnalytics'
import { useFeatureFlag } from '../../optimizely/hooks/useFeatureFlag'
import { OptimizelyFeatureFlags } from '../../../models/Optimizely'
import { useMParticleEventProperties } from './useMParticleEventProperties'

export interface TrackEventFunction {
  (
    eventName: string,
    eventType: EventType,
    eventInfo?: {
      [key: string]: number | string | boolean | null | undefined
    },
    customFlags?: SDKEventCustomFlags,
    eventOptions?: SDKEventOptions,
  ): boolean
}

type TrackFunnelEventFn = TrackEventFunction

/**
 * Used to track funnel events with comprehensive mParticle event properties.
 * Similar to useTrackUIElementEvent but includes all properties from useMParticleEventProperties
 * instead of just npo_id, npo_name, and campaign_id.
 */
function useTrackFunnelEvent(): TrackFunnelEventFn {
  const trackEvent = useTrackEvent()
  const mParticleEventProperties = useMParticleEventProperties()

  const { mParticle, consentSynced } = useInternalAnalytics()
  const isMparticleEnabled = useFeatureFlag(OptimizelyFeatureFlags.classy_mparticle).enabled

  const trackFunnelEvent: TrackEventFunction = useCallback(
    (eventName, eventType, eventInfo, customFlags, eventOptions) => {
      if (!isMparticleEnabled || !mParticle || !consentSynced) {
        return false
      }

      const eventInfoWithCommonProperties: mParticleSdk.SDKEventAttrs = {
        ...eventInfo,

        npo_id: mParticleEventProperties.npo_id,
        npo_name: mParticleEventProperties.npo_name,
        campaign_id: mParticleEventProperties.campaign_id,

        ...mParticleEventProperties.donation_components,
        ...mParticleEventProperties.donation_selections,
      }

      const customFlagsWithCommonFlags = {
        ...customFlags,
        'GA4.Title': document.title,
        'GA4.Location': document.location.href,
        'GA4.Referrer': document.referrer,
      }

      trackEvent(
        eventName,
        eventType,
        eventInfoWithCommonProperties,
        customFlagsWithCommonFlags,
        eventOptions,
      )

      return true
    },
    [mParticle, consentSynced, isMparticleEnabled, mParticleEventProperties, trackEvent],
  )

  return trackFunnelEvent
}

export default useTrackFunnelEvent
